// Outside-the-US sign-up notice (Oct 10, 2026 — Dennis, Oct 9: "if it works, then
// great. I would not want to provide support to users in other countries if TTO was
// not going to work there."). Never a block: the visitor reads what doesn't work
// abroad and chooses Continue or Cancel. Asked once per page load, before the
// account is created. If the country can't be told (lookup failed, private network),
// nothing is shown.
//
// Use: const notice = useOutsideUSNotice(API_URL);
//      onSubmit → notice.guard(() => createTheAccount());
//      body     → outsideNoticeAccepted: notice.accepted()
//      render   → {notice.modal}
import React, { useEffect, useRef, useState } from 'react';

export function useOutsideUSNotice(apiUrl) {
  const [where, setWhere] = useState(null);
  const [pending, setPending] = useState(null);
  const acceptedRef = useRef(false);

  useEffect(() => {
    let off = false;
    fetch(`${apiUrl}/auth/where`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!off) setWhere(d); })
      .catch(() => { /* unknown: no notice */ });
    return () => { off = true; };
  }, [apiUrl]);

  const guard = (proceed) => {
    if (where && where.notice && !acceptedRef.current) { setPending(() => proceed); return; }
    proceed();
  };

  const modal = pending ? (
    <OutsideUSNotice
      countryName={where && where.country_name}
      onContinue={() => { acceptedRef.current = true; const p = pending; setPending(null); p(); }}
      onCancel={() => setPending(null)}
    />
  ) : null;

  return { guard, modal, accepted: () => acceptedRef.current, where };
}

export default function OutsideUSNotice({ countryName, onContinue, onCancel }) {
  const li = { margin: '0 0 8px', lineHeight: 1.45 };
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="outside-us-title" data-testid="outside-us-notice"
      style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 480, width: '100%', padding: '24px 22px', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 8 }} aria-hidden="true">🌍</div>
        <h2 id="outside-us-title" style={{ fontSize: 22, fontWeight: 800, color: '#14532d', margin: '0 0 10px' }}>Signing up from outside the US?</h2>
        <p style={{ fontSize: 17, color: '#1f2937', margin: '0 0 12px', lineHeight: 1.45 }}>
          It looks like you may be signing up from {countryName || 'outside the United States'}.
          Tee Time Organizer is built for golf groups in the United States and Canada, and a few things only work there:
        </p>
        <ul style={{ fontSize: 16, color: '#374151', margin: '0 0 12px', paddingLeft: 22 }}>
          <li style={li}>Reminders go out on US time zones.</li>
          <li style={li}>Text messages reach US and Canadian phone numbers only.</li>
          <li style={li}>GHIN handicap sync is for USGA golfers.</li>
        </ul>
        <p style={{ fontSize: 16, color: '#374151', margin: '0 0 18px', lineHeight: 1.45 }}>
          You're welcome to continue, but our support outside the US and Canada is limited.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onCancel}
            style={{ flex: 1, height: 52, borderRadius: 12, border: '2px solid #9ca3af', background: '#fff', color: '#374151', fontSize: 17, fontWeight: 700, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={onContinue} autoFocus
            style={{ flex: 1, height: 52, borderRadius: 12, border: 'none', background: '#166534', color: '#fff', fontSize: 17, fontWeight: 700, cursor: 'pointer' }}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
